import React, { useState, useEffect, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { Action } from '../../types/actionTypes'
import { getActionbyId, updateAction } from '../../redux/actions/actionActions'
import UseAlert from '../../components/common/alert'
import SelectPriority from '../../components/common/SelectPriority'
import SelectProjet from '../projets/SelectProjet'
import { ProjectSummaryCard } from '../../components/quickCreate/ParentSummaryCard'
import { projectLimitViolation, useProjectBudget } from '../../components/quickCreate/parentInfo'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import { formatTableDate } from '../../utils/formatDate'
import DateSelector from '../../components/common/Date'
import SelectTypeAction from '../../components/common/SelectTypeAction'
import SelectStatut from '../../components/common/SelectStatut'
import { selectActeurAction } from '../../redux/actorAction/actorActionSelectors'
import { fetchActeursByActionId } from '../../redux/actorAction/actorActionAction'
import { selectDocument } from '../../redux/documents/documentSelector'
import { fetchDocumentById } from '../../redux/documents/documentAction'
import DocumentSelector from '../../components/common/DocumentSelector'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'
import '../../i18n'
import NumberInput from '../../components/common/NumberInput'

export default function ActionUpdate() {
  const [priority, setPriority] = useState<number>(1)
  const actor = useSelector(selectActeurAction)
  const [status, setStatus] = useState<string>('EnCours')
  const [TypeAction, setTypeAction] = useState<string>('')
  const [DateDebut, setDateDebut] = useState<Date>(new Date())
  const [DateFin, setDateFin] = useState<Date>(new Date())
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const [action, setAction] = useState<Action>({
    _id: '',
    ActionId: '',
    ActionName: '',
    Description: '',
    DateDebut: new Date(),
    DateFin: new Date(),
    Status: '',
    Priority: 0,
    Cout: 0,
    CoutTotal: 0,
    DateFinSupp: new Date(),
    MissionId: '',
    TypeAction: '',
    Support: null,
  })

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false })
    window.history.back()
  }

  const dispatch: AppDispatch = useAppDispatch()
  const actionsList = useSelector((state: RootState) => state.action.action)
  const projectBudget = useProjectBudget((action.ProjetId as string) || undefined)
  const fmt = useNumberFormat()
  const loading = useSelector((state: RootState) => state.action.loading)
  const error = useSelector((state: RootState) => state.action.error)
  const document = useSelector(selectDocument)
  const { id } = useParams<{ id: string }>()

  useEffect(() => {
    if (id) {
      dispatch(fetchActeursByActionId(id))
      dispatch(getActionbyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (actionsList?.Support && typeof actionsList.Support === 'string') {
      dispatch(fetchDocumentById(actionsList.Support))
    }
  }, [dispatch, actionsList?.Support])

  useEffect(() => {
    if (actionsList !== null) {
      setAction({
        _id: actionsList._id,
        ActionId: actionsList.ActionId,
        ActionName: actionsList.ActionName,
        Description: actionsList.Description,
        DateDebut: actionsList.DateDebut,
        DateFin: actionsList.DateFin,
        Status: actionsList.Status,
        Priority: actionsList.Priority,
        Cout: actionsList.Cout,
        CoutTotal: actionsList.CoutTotal,
        DateFinSupp: actionsList.DateFinSupp,
        MissionId: actionsList.MissionId,
        TypeAction: actionsList.TypeAction,
        ProjetId: actionsList.ProjetId,
        ProjetName: actionsList.ProjetName,
        Support: actionsList.Support,
      })
      setPriority(actionsList.Priority as number)
      setStatus(actionsList.Status || '')
      setTypeAction(actionsList.TypeAction || '')
      setDateDebut(actionsList.DateDebut || new Date())
      setDateFin(actionsList.DateFin || new Date())
    }
  }, [actionsList])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setAction((prev) => ({ ...prev, [name]: value }))
  }

  const handleDateChange = (dates: [Date, Date] | null) => {
    if (dates) {
      const [start, end] = dates
      setAction((prev) => ({
        ...prev,
        DateDebut: start,
        DateFin: end,
      }))
    }
  }

  const createStringHandler =
    (
      setStringState: React.Dispatch<React.SetStateAction<string>>,
      property: string
    ) =>
    (event: React.ChangeEvent<{ value: string }>) => {
      const { value } = event.target
      setStringState(value)
      setAction((prev) => ({ ...prev, [property]: value }))
    }

  const createNumberHandler =
    (
      setNumberState: React.Dispatch<React.SetStateAction<number>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: number }>) => {
      const { value } = event.target
      setNumberState(value)
      setAction((prev) => ({ ...prev, [property]: value }))
    }

  const handlePriorityChange = createNumberHandler(setPriority, 'Priority') as (
    event: React.ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void
  const handleTypeAction = createStringHandler(setTypeAction, 'TypeAction') as (
    event: SelectChangeEvent<string>
  ) => void
  const handleStatusChange = createStringHandler(setStatus, 'Status') as (
    event: SelectChangeEvent<string>
  ) => void

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    setAction((prev) => ({
      ...prev,
      ProjetId: event.target.value,
    }))
  }

  const handleUpdateAction = async () => {
    const limit = projectLimitViolation(projectBudget.data, {
      cost: Number(action.Cout) || 0,
      start: action.DateDebut,
      end: action.DateFin,
      countedCost:
        action.ProjetId && action.ProjetId === actionsList?.ProjetId
          ? Number(actionsList?.Cout) || 0
          : 0,
    })
    if (limit && projectBudget.data) {
      const info = projectBudget.data
      const alreadyCounted =
        action.ProjetId && action.ProjetId === actionsList?.ProjetId
          ? Number(actionsList?.Cout) || 0
          : 0
      const ceiling = info.remaining + alreadyCounted
      setAlert({
        open: true,
        message:
          limit === 'cost'
            ? t('parentLimits.costOver', { available: fmt.money(ceiling) })
            : t('parentLimits.datesOutside', {
                start: formatTableDate(info.projet.DateDebut, i18n.language),
                end: formatTableDate(info.projet.DateFin, i18n.language),
              }),
        severity: 'error',
      })
      return
    }
    try {
      if (action._id) {
        await dispatch(
          updateAction({
            updatedAction: action,
            _id: action._id,
          })
        )
        setAlert({
          open: true,
          message: t('alerts.actionUpdate.success'),
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: t('alerts.actionUpdate.emptyFields'),
          severity: 'error',
        })
      }
    } catch (error: unknown) {
      console.error(
        'Error updating task:',
        error instanceof Error ? error.message : 'Unknown error'
      )
      setAlert({
        open: true,
        message: t('alerts.actionUpdate.error'),
        severity: 'error',
      })
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  const submitLabel = t('actionCreate.updateSubmit')
  const formSteps = [
    {
      title: t('actionCreate.sections.identification.title'),
      fieldCount: 2,
    },
    { title: t('actionCreate.sections.origin.title'), fieldCount: 1 },
    { title: t('actionCreate.sections.proof.title'), fieldCount: 1 },
    { title: t('actionCreate.sections.planning.title'), fieldCount: 5 },
  ]

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('actionCreate.updateEyebrow')}
        title={t('Update task')}
        subtitle={t('actionCreate.updateSubtitle')}
        backLabel={t('actionCreate.backToList')}
        onBack={() => navigate('/actions')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('actionCreate.sections.identification.title')}
            >
              <FormGrid cols={1}>
                <FormField
                  label={t('actionCreate.sections.identification.taskName')}
                  required
                >
                  <input
                    className="incyia-form-inp"
                    name="ActionName"
                    value={action.ActionName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField
                  label={t(
                    'actionCreate.sections.identification.description'
                  )}
                  required
                >
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={action.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('actionCreate.sections.origin.title')}
              description={t('actionCreate.sections.origin.desc')}
            >
              <FormGrid cols={3}>
                <FormField
                  label={t('actionCreate.sections.origin.project')}
                >
                  <SelectProjet
                    value={action.ProjetId as string}
                    onChange={handleProjectChange}
                    MissionId={action.MissionId as string}
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('actionCreate.sections.proof.title')}
              description={t('actionCreate.sections.proof.desc')}
            >
              <FormGrid cols={1}>
                <FormField label={t('actionCreate.sections.proof.upload')}>
                  <DocumentSelector
                    label=""
                    file={action.Support || null}
                    displayFileName={document?.originalName}
                    previewUrl={document?.path}
                    onFileChange={(file) => {
                      setAction((prev) => ({ ...prev, Support: file }))
                    }}
                    fileType="application/pdf"
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection title={t('actionCreate.sections.planning.title')}>
              <FormGrid cols={3}>
                <FormField
                  label={t('actionCreate.sections.planning.dates')}
                  span
                  help={
                    projectBudget.data?.projet.DateDebut &&
                    projectBudget.data?.projet.DateFin
                      ? t('parentLimits.datesOutside', {
                          start: formatTableDate(
                            projectBudget.data.projet.DateDebut,
                            i18n.language
                          ),
                          end: formatTableDate(
                            projectBudget.data.projet.DateFin,
                            i18n.language
                          ),
                        })
                      : undefined
                  }
                >
                  <DateSelector
                    placeholder={t('actionCreate.sections.planning.dates')}
                    onDateChange={handleDateChange}
                    defaultValue={[new Date(DateDebut), new Date(DateFin)]}
                    hideLabel
                    minDate={
                      projectBudget.data?.projet.DateDebut
                        ? new Date(projectBudget.data.projet.DateDebut)
                        : null
                    }
                    maxDate={
                      projectBudget.data?.projet.DateFin
                        ? new Date(projectBudget.data.projet.DateFin)
                        : null
                    }
                  />
                </FormField>
                <FormField
                  label={t('actionCreate.sections.planning.cost')}
                  required
                  help={t('actionCreate.sections.planning.costHelp')}
                >
                  <NumberInput
                    currency
                    min={0}
                    name="Cout"
                    value={action.Cout}
                    onChange={(v) => setAction((a: any) => ({ ...a, Cout: v ?? 0 }))}
                    required
                  />
                </FormField>
                <FormField
                  label={t('actionCreate.sections.planning.type')}
                >
                  <SelectTypeAction
                    value={TypeAction}
                    onChange={handleTypeAction}
                    hideLabel
                  />
                </FormField>
                <FormField
                  label={t('actionCreate.sections.planning.priority')}
                >
                  <SelectPriority
                    value={priority}
                    onChange={handlePriorityChange}
                    hideLabel
                  />
                </FormField>
                <FormField
                  label={t('actionCreate.sections.planning.status')}
                >
                  <SelectStatut
                    value={status}
                    onChange={handleStatusChange}
                    actor={actor}
                    hideLabel
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark')}
              cancelLabel={t('buttons.cancel', { defaultValue: 'Cancel' })}
              submitLabel={submitLabel}
              onCancel={() => navigate(-1)}
              onSubmit={handleUpdateAction}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm')}
            tipTitle={t('actionCreate.aside.goodToKnow')}
            tip={t('actionCreate.aside.tip')}
            context={
              action.ProjetId ? (
                <>
                  <ProjectSummaryCard
                    plain
                    info={projectBudget.data}
                    status={projectBudget.status}
                  />
                  <p className="incyia-aside-note incyia-aside-divider">
                    {t('parentLimits.stayInside')}
                  </p>
                </>
              ) : undefined
            }
            steps={formSteps}
            requiredCount={3}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', { count, submit })
            }
          />
        }
      />
    </div>
  )
}
